import TiclickTable from '@src/design-system/molecules/ticlick-table';
import { Tabs, TabsList, TabsTrigger } from '@src/design-system/tabs';
import { PageContainer } from '@src/shared/components/page-container';
import { PageTitle } from '@src/shared/components/page-title';
import { ArrowDown2, Clock, Diagram, People, ProfileAdd } from 'iconsax-react';
import {
  useFollowUpsTable,
  type FollowUpsTab,
} from './table/use-follow-ups-table';
import leadNoteApi from '../../services/lead-note-api';
import { StatCard } from '@src/features/dashboard/components/stats-card';
import { cn } from '@src/design-system/helpers';
import { useState } from 'react';

export function LeadsFollowUps() {
  const [followUpsTab, setFollowUpsTab] = useState<FollowUpsTab>('unresolved');
  const { table, actionsTable } = useFollowUpsTable(followUpsTab);
  const { data: leadNotesStats, isPending: statsLoading } =
    leadNoteApi.useGetLeadNotesStats();
  const [isDashboardOpen, setIsDashboardOpen] = useState(true);

  const followUpStatRows = [
    {
      key: 'today',
      title: 'Today',
      Icon: ProfileAdd,
      value: leadNotesStats?.today ?? 0,
      color: 'text-green-500',
    },
    {
      key: 'tomorrow',
      title: 'Tomorrow',
      Icon: Diagram,
      value: leadNotesStats?.tomorrow ?? 0,
      color: 'text-secondary-1000',
    },
    {
      key: 'this_week',
      title: 'This week',
      Icon: Clock,
      value: leadNotesStats?.this_week ?? 0,
      color: 'text-primary-800',
    },
    {
      key: 'overdue',
      title: 'Overdue',
      Icon: People,
      value: leadNotesStats?.overdue ?? 0,
      color: 'text-red-400',
    },
  ] as const;

  return (
    <PageContainer>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2.5'>
          <PageTitle titles={['Leads', 'Follow Ups']} />
        </div>
        <ArrowDown2
          onClick={() => setIsDashboardOpen((prev) => !prev)}
          color='#584777'
          size={40}
          className={`cursor-pointer border border-primary-200 rounded-full p-2.5 ${isDashboardOpen ? 'rotate-180' : 'rotate-0'}`}
        />
      </div>
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 ${isDashboardOpen ? '' : 'hidden'}`}
      >
        {followUpStatRows.map(({ key, title, value, Icon, color }) => (
          <StatCard
            key={key}
            title={title}
            className='h-[115px] border border-[#DEE0E3]'
            value={<span className={cn(' font-bold', color)}>{value}</span>}
            icon={
              <Icon
                className='text-primary-1000'
                size={24}
              />
            }
            isLoading={statsLoading}
          />
        ))}
      </div>
      <div className='p-4 pt-0 rounded-lg border bg-white border-pale-border [&_td]:text-gray-900 [&_th]:text-gray-800 [&_th]:h-12 [&_th]:font-bold [&_th]:text-sm'>
        <Tabs
          value={followUpsTab}
          onValueChange={(value) => setFollowUpsTab(value as FollowUpsTab)}
          className='w-full'
        >
          <TabsList className='mb-4 w-full border-b border-primary-200 pb-0 h-auto gap-1'>
            <TabsTrigger
              className='border-b-0'
              value='unresolved'
            >
              Unresolved
            </TabsTrigger>
            <TabsTrigger
              className='border-b-0'
              value='resolved'
            >
              Resolved
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <TiclickTable
          key={followUpsTab}
          props={{ searchInputProps: { queryKey: 'q' } }}
          withFilterBtn
          table={table}
          actions={actionsTable}
        />
      </div>
    </PageContainer>
  );
}

export default LeadsFollowUps;
